import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { WebSocket } from 'partysocket';
import { WebsiteContext } from './WebsiteProvider';
import { VendorContext } from './VendorProvider';
import { UserContext } from './UserProvider';
import { NotificationContext } from './NotificationProvider';
import { VendorFileContext } from './VendorFileProvider';
import { WebsiteFileContext } from './WebsiteFileProvider';
import { ContactContext } from './ContactProvider';

const WebSocketContext = createContext(null);
const API_URL = process.env.REACT_APP_WS_URL;

export const WebSocketProvider = ({ children }) => {
  const { token, username } = useContext(UserContext);

  const {
    getWebsiteComments,
    selectedWebsite,
    setCallWebsiteForRefresh,
    getAllWebsites,
    setWebsites,
    setVendorWebsiteData,
  } = useContext(WebsiteContext);

  const {
    getVendorComments,
    selectedVendor,
    setCallVendorForRefresh,
    getAllVendors,
    setVendors,
    getVendorWebsites,
  } = useContext(VendorContext);

  const {
    getContacts,
    setContacts,
    selectedContact,
    setCallContactForRefresh,
    showContactModalRef,
    setUpdatedContactId,
  } = useContext(ContactContext);

  const { getAllNotifications, setNotifications } = useContext(NotificationContext);
  const { getVendorFiles } = useContext(VendorFileContext);
  const { getWebsiteFiles } = useContext(WebsiteFileContext);

  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const selectedWebsiteRef = useRef(selectedWebsite);
  const selectedVendorRef = useRef(selectedVendor);
  const selectedContactRef = useRef(selectedContact);

  useEffect(() => {
    selectedWebsiteRef.current = selectedWebsite;
  }, [selectedWebsite]);

  useEffect(() => {
    selectedVendorRef.current = selectedVendor;
  }, [selectedVendor]);

  useEffect(() => {
    selectedContactRef.current = selectedContact;
  }, [selectedContact]);

  const handleCommentMessage = async (data) => {
    if (data.context === 'website' && data.siteId === selectedWebsiteRef.current?.rowKey) {
      await getWebsiteComments(data.siteId);
    } else if (data.context === 'vendor' && data.vendorId === selectedVendorRef.current?.rowKey) {
      await getVendorComments(data.vendorId);
    }
  };

  const handleWebsiteMessage = async (data) => {
    // Handle selected website refresh
    if (data.siteId === selectedWebsiteRef.current?.rowKey) {
      setCallWebsiteForRefresh(true);
    }

    // Handle vendor websites update for both delete and update actions
    if (data.context === 'vendor' && data.vendorId && (data.action === 'delete' || data.action === 'update')) {
      try {
        console.log('Fetching vendor websites for vendorId:', data.vendorId);
        const vendorWebsites = await getVendorWebsites(data.vendorId);
        console.log('Received vendor websites data:', vendorWebsites);
        if (vendorWebsites) {
          setVendorWebsiteData(vendorWebsites);
          console.log('Vendor website data updated in context');
        } else {
          console.warn('No vendor websites data returned');
        }
      } catch (error) {
        console.error('Error updating vendor websites:', error);
      }
    }

    // Update the full websites list
    try {
      const websites = await getAllWebsites();
      setWebsites(websites);
    } catch (error) {
      console.error('Error updating all websites:', error);
    }
  };

  const handleVendorMessage = async (data) => {
    if (selectedVendorRef.current && data.vendorId === selectedVendorRef.current.rowKey) {
      setCallVendorForRefresh(true);
    }
    const vendors = await getAllVendors();
    setVendors(vendors);
  };

  const handleContactMessage = async (data) => {
    if (data.vendorId === selectedVendorRef.current?.rowKey) {
      setUpdatedContactId(data.id);
      if (showContactModalRef.current && selectedContactRef.current?.rowKey === data.id) {
        setCallContactForRefresh(true);
      } else {
        const contacts = await getContacts(data.vendorId);
        setContacts(contacts);
      }
    }
  };

  const handleNotificationMessage = async (data) => {
    if (data.username === username) {
      const notifications = await getAllNotifications();
      setNotifications(notifications);
    }
  };

  const handleDocumentMessage = async (data) => {
    if (data.context === 'vendor' && data.vendorId === selectedVendorRef.current?.rowKey) {
      await getVendorFiles(data.vendorId);
    } else if (data.context === 'website' && data.siteId === selectedWebsiteRef.current?.rowKey) {
      await getWebsiteFiles(data.siteId);
    }
  };

  useEffect(() => {
    const options = {
      connectionTimeout: 1000,
      maxRetries: 10,
      reconnectionDelayGrowFactor: 1,
    };

    ws.current = new WebSocket(API_URL, [], options);

    ws.current.onopen = () => {
      ws.current.send(JSON.stringify({ token }));
      setIsConnected(true);
      console.log('WebSocket connection opened');
    };

    ws.current.onmessage = async (message) => {
      console.log('Received WebSocket message:', message.data);

      if (message.data === 'Subscribed Successfully') {
        console.log('Subscription successful');
        return;
      }

      try {
        const data = JSON.parse(message.data);
        console.log('Parsed WebSocket data:', data);

        if (data.type !== 'website' && data.username && data.username !== username) {
          console.log('Ignoring message for different user:', data.username);
          return;
        }

        console.log('Processing WebSocket message type:', data.type);

        switch (data.type) {
          case 'comment':
            await handleCommentMessage(data);
            break;
          case 'website':
            console.log('Handling website message:', data);
            await handleWebsiteMessage(data);
            break;
          case 'vendor':
            await handleVendorMessage(data);
            break;
          case 'contact':
            await handleContactMessage(data);
            break;
          case 'notification':
            await handleNotificationMessage(data);
            break;
          case 'document':
            await handleDocumentMessage(data);
            break;
          default:
            console.log('Unknown message type:', data.type);
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error, message.data);
      }
    };

    ws.current.onclose = () => {
      setIsConnected(false);
      console.log('WebSocket connection closed');
    };

    return () => {
      ws.current.close();
    };
  }, [token]);

  return (
    <WebSocketContext.Provider value={{ ws: ws.current, isConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);